import { apiHelpers } from '@/services/api';

// Event category type - must match backend enum
export type EventCategory = 'PARTY' | 'GAMING' | 'SPORT' | 'FOOD' | 'OTHER';

// Participant status type
export type ParticipantStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'MAYBE';

// User profile info
export interface UserProfile {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

// User with profile
export interface UserWithProfile {
  id: string;
  email: string;
  profile: UserProfile | null;
}

// Participant with user info
export interface ParticipantWithUser {
  eventId: string;
  userId: string;
  status: ParticipantStatus;
  user: UserWithProfile;
}

// Event with full details
export interface EventWithDetails {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  locationUrl: string | null;
  category: EventCategory;
  isRecurring: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator: UserWithProfile;
  participants: ParticipantWithUser[];
  _count: {
    comments: number;
  };
}

// Event comment
export interface EventComment {
  id: string;
  eventId: string;
  userId: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  user: UserWithProfile;
}

// Event filters for list
export interface EventFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  category?: EventCategory;
  startDate?: string;
  endDate?: string;
  search?: string;
  isParticipant?: boolean;
}

// Create event input
export interface CreateEventInput {
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  locationUrl?: string;
  category: EventCategory;
  isRecurring?: boolean;
  participantIds?: string[];
}

// Update event input
export interface UpdateEventInput {
  title?: string;
  description?: string | null;
  startTime?: string;
  endTime?: string;
  location?: string | null;
  locationUrl?: string | null;
  category?: EventCategory;
  isRecurring?: boolean;
}

// Paginated response
export interface PaginatedEventsResponse {
  events: EventWithDetails[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// Comments paginated response
export interface PaginatedCommentsResponse {
  comments: EventComment[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// Add participant input
export interface AddParticipantInput {
  userId: string;
  status?: ParticipantStatus;
}

// Update RSVP input
export interface UpdateRsvpInput {
  status: ParticipantStatus;
}

// Add comment input
export interface AddCommentInput {
  comment: string;
}

// Event Service
export const eventService = {
  /**
   * Get list of events with filters
   */
  async getEvents(filters?: EventFilters): Promise<PaginatedEventsResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.category) params.category = filters.category;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.search) params.search = filters.search;
      if (filters.isParticipant !== undefined) params.isParticipant = filters.isParticipant;
    }

    return apiHelpers.get<PaginatedEventsResponse>('/events', params);
  },

  /**
   * Get single event by ID
   */
  async getEventById(id: string): Promise<EventWithDetails> {
    return apiHelpers.get<EventWithDetails>(`/events/${id}`);
  },

  /**
   * Create a new event
   */
  async createEvent(data: CreateEventInput): Promise<EventWithDetails> {
    return apiHelpers.post<EventWithDetails>('/events', data);
  },

  /**
   * Update an event
   */
  async updateEvent(id: string, data: UpdateEventInput): Promise<EventWithDetails> {
    return apiHelpers.patch<EventWithDetails>(`/events/${id}`, data);
  },

  /**
   * Delete an event
   */
  async deleteEvent(id: string): Promise<void> {
    return apiHelpers.delete(`/events/${id}`);
  },

  /**
   * Add a participant to an event
   */
  async addParticipant(eventId: string, data: AddParticipantInput): Promise<ParticipantWithUser> {
    return apiHelpers.post<ParticipantWithUser>(`/events/${eventId}/participants`, data);
  },

  /**
   * Update participant RSVP status
   */
  async updateRsvpStatus(eventId: string, userId: string, data: UpdateRsvpInput): Promise<ParticipantWithUser> {
    return apiHelpers.patch<ParticipantWithUser>(`/events/${eventId}/participants/${userId}`, data);
  },

  /**
   * Remove a participant from an event
   */
  async removeParticipant(eventId: string, userId: string): Promise<void> {
    return apiHelpers.delete(`/events/${eventId}/participants/${userId}`);
  },

  /**
   * Get event comments
   */
  async getComments(eventId: string, page = 1, limit = 20): Promise<PaginatedCommentsResponse> {
    return apiHelpers.get<PaginatedCommentsResponse>(`/events/${eventId}/comments`, { page, limit });
  },

  /**
   * Add a comment to an event
   */
  async addComment(eventId: string, data: AddCommentInput): Promise<EventComment> {
    return apiHelpers.post<EventComment>(`/events/${eventId}/comments`, data);
  },

  /**
   * Delete a comment
   */
  async deleteComment(eventId: string, commentId: string): Promise<void> {
    return apiHelpers.delete(`/events/${eventId}/comments/${commentId}`);
  },
};

export default eventService;
